<script setup lang="ts">
import type { LawAndCommentary } from '~/utils/solr/law-and-commentary'
import { faXmark } from '@fortawesome/pro-regular-svg-icons'

const props = defineProps<{
  lawCommentId: number | null | undefined
  searchMode: boolean
}>()

const emit = defineEmits<{
  showSearchResult: [string, string[], boolean, number | null | undefined]
  searchResultsLoaded: [Array<{ id: string, highlights: string[], isComment: boolean, ezMainNodeId?: number | null }>]
}>()

const trimHighlightSnippet = useTrimHighlightSnippet()

const lawFilterSelected = ref<boolean>(false)
const commentFilterSelected = ref<boolean>(false)
const searchInput = useTemplateRef('searchInput')

const params = useLawRouteParams()

const { searchTerm, commit, reset } = useLawSearchQuery()

const additionalParams = computed(() => ({
  lawId: !commentFilterSelected.value && params.value.lawId ? params.value.lawId : undefined,
  commentId: !lawFilterSelected.value && props.lawCommentId !== null ? props.lawCommentId : undefined,
}))

const { searchResult, allMatchedDocs, isLoading } = useLawAndCommentarySearch(searchTerm, additionalParams)

const results = computed(() => searchResult?.value?.response.docs || [])

watch(allMatchedDocs, (docs) => {
  emit('searchResultsLoaded', docs.map(doc => ({
    id: doc.id,
    highlights: doc.highlights.map(h => h.text),
    isComment: isCommentary(doc),
    ezMainNodeId: doc.ezMainNodeId,
  })))
}, { immediate: true })

watch(searchInput, (newValue) => {
  if (newValue) {
    newValue.focus()
  }
})

function highlightKeys(docs: typeof results.value) {
  return new Map(docs.map(doc => [doc.id, JSON.stringify(doc.highlights.map(h => h.text))]))
}

// Seeded from the cached list so the first refetch compares against what is already shown.
let previousHighlightKeys = highlightKeys(results.value)

watch(results, (docs) => {
// Compare with the previous list to avoid re-emitting unchanged hits and resetting their scroll.
  const currentHighlightKeys = highlightKeys(docs)
  for (const doc of docs) {
    if (activeHighlight(handleLink(doc)) && previousHighlightKeys.get(doc.id) !== currentHighlightKeys.get(doc.id)) {
      handleShowSearchResult(doc.id, doc.highlights.map(h => h.text), isCommentary(doc), doc.ezMainNodeId)
    }
  }
  previousHighlightKeys = currentHighlightKeys
})

function handleFilter(filterType: 'law' | 'comment') {
  if (filterType === 'law') {
    lawFilterSelected.value = !lawFilterSelected.value
    commentFilterSelected.value = false
  }
  else {
    commentFilterSelected.value = !commentFilterSelected.value
    lawFilterSelected.value = false
  }
}

function handleShowSearchResult(nodeId: string, highlights: string[], lawOrComment: boolean, ezMainNodeId?: number | null) {
  if (!nodeId) {
    return
  }
  emit('showSearchResult', nodeId, highlights, lawOrComment, ezMainNodeId)
}

function isCommentary(doc: LawAndCommentary | undefined | null): boolean {
  return !!doc?.lawFragmentId || !!doc?.lawId
}

function activeHighlight(nodeId: string | undefined): boolean {
  return `${params.value.lawId}/${params.value.lawFragmentId}` === nodeId?.toLocaleLowerCase()
}

function handleLink(doc: LawAndCommentary | undefined): string {
  if (!doc?.id) {
    return ''
  }
  return isCommentary(doc) ? `${params.value.lawId}/${doc?.lawFragmentId}` : doc.id
}
</script>

<template>
  <div data-search-sticky class="sticky top-12 z-[8] pt-s pb-m bg-surface-elevated flex flex-col gap-m">
    <div class="bg-surface-elevated hidden md:flex gap-2xs flex-col">
      <label for="law-commentary" class="text-caption md:text-small font-normal">Søk i lov og lovkommentar</label>
      <FormSearchInput
        id="law-commentary"
        ref="searchInput"
        v-model="searchTerm"
        shape="square"
        size="medium"
        name="lcq"
        color="carbon"
        external-label
        resettable
        @reset="reset"
        @update:model-value="commit"
      />
    </div>
    <div class="flex items-center gap-3xs w-full">
      <div v-if="isLoading" class="flex-1">
        <Skeleton class="h-4 w-2/4" />
      </div>
      <span v-else class="text-caption flex-1" aria-live="polite" aria-atomic="true">Viser {{ searchResult?.response.numFound ?? 0 }} treff i</span>
      <div v-if="lawCommentId" class="flex gap-2xs flex-wrap">
        <Chip
          size="small"
          :icon-left="lawFilterSelected ? faXmark : undefined"
          :variant="lawFilterSelected ? 'filled' : 'outlined'"
          color="carbon"
          :aria-pressed="lawFilterSelected"
          @click="handleFilter('law')"
        >
          Lov
        </Chip>
        <Chip
          size="small"
          :icon-left="commentFilterSelected ? faXmark : undefined"
          :variant="commentFilterSelected ? 'filled' : 'outlined'"
          color="carbon"
          :aria-pressed="commentFilterSelected"
          @click="handleFilter('comment')"
        >
          Lovkommentar
        </Chip>
      </div>
    </div>
  </div>
  <div class="flex flex-col gap-xs">
    <span class="sr-only">Søkeresultat</span>
    <template v-if="isLoading">
      <div v-for="n in 6" :key="n" class="hover:bg-surface-secondary hover:border-b-0 hover:rounded group transition-all duration-300 ease-in-out border-b border-b-border-secondary animate-pulse">
        <div class="flex flex-col gap-2xs px-s pt-xs pb-s">
          <Skeleton class="h-4 rounded w-1/4" />
          <Skeleton class="h-6 rounded w-3/4" />
          <Skeleton class="h-4 rounded w-full" />
        </div>
      </div>
    </template>
    <template v-else>
      <template v-for="doc in results" :key="doc.id">
        <div
          class="hover:bg-surface-secondary hover:border-b-0 hover:rounded group transition-all duration-300 ease-in-out"
          :class="activeHighlight(handleLink(doc)) ? 'active-highlight' : 'border-b-border-secondary border-b dark:border-b-border-default'"
        >
          <LawLink
            class="flex flex-col gap-2xs search-result no-underline text-xsmall px-s pt-xs pb-s dark:text-white"
            :law="handleLink(doc)"
            @click="handleShowSearchResult(doc.id, doc.highlights.map(h => h.text), isCommentary(doc), doc.ezMainNodeId)"
          >
            <Chip class="text-caption px-3xs" variant="neutral" color="carbon" size="small">
              {{ isCommentary(doc) ? 'Lovkommentar' : 'Lov' }}
            </Chip>
            <div class="flex flex-col gap-3xs">
              <template v-if="doc.highlights.some(h => h.field === 'title')">
                <p
                  v-for="highlight in doc.highlights.filter(h => h.field === 'title')"
                  :key="highlight.text"
                  class="text-small"
                  v-html="highlight.text"
                />
              </template>
              <template v-else>
                <p class="text-small">
                  {{ doc.shortTitle || doc.title || doc.paragraphTitle || doc.chapterTitle }}
                </p>
              </template>
              <template v-if="doc.highlights.some(h => h.field === 'bodytext')">
                <span
                  v-for="highlight in doc.highlights.filter(h => h.field === 'bodytext')"
                  :key="highlight.text"
                  class="text-xsmall search-result-body"
                  v-html="trimHighlightSnippet(highlight.text)"
                />
              </template>
              <template v-else-if="doc.bodytext">
                <span class="text-xsmall search-result-body line-clamp-2" v-html="trimHighlightSnippet(doc.bodytext)" />
              </template>
            </div>
          </LawLink>
        </div>
      </template>
    </template>
  </div>
</template>
